// "Not at war" hint for a Firestorm that hit nothing. The snapshot does not
// carry diplomatic stances, and humans cannot declare war, so the client uses
// what it does have: a blast that hit 0 while the nearest thing under it
// belongs to another real player (a player entry in the globals, not a
// bandit/pirate band) almost always means "not hostile". Pure and
// framework-free so it is unit-testable.

import type { GameSnapshot, SceneNode } from '@aod/protocol';

export type CastHint = 'notAtWar';

function hexDistance(aq: number, ar: number, bq: number, br: number): number {
  const dq = aq - bq;
  const dr = ar - br;
  return Math.max(Math.abs(dq), Math.abs(dr), Math.abs(dq + dr));
}

function coord(n: SceneNode): { q: number; r: number; owner: number } | null {
  const q = n.props?.q;
  const r = n.props?.r;
  const owner = n.props?.ownerId;
  if (typeof q !== 'number' || typeof r !== 'number' || typeof owner !== 'number') return null;
  return { q, r, owner };
}

/**
 * A hint for a cast at (q, r) that dealt `hits` damage events, or null when
 * no hint applies (it hit something, nothing was there, or the nearest
 * owned thing is the caster's own / a band that is always hostile).
 */
export function castHint(
  snapshot: GameSnapshot | null,
  localPlayer: number,
  q: number,
  r: number,
  radius: number,
  hits: number | null,
): CastHint | null {
  if (hits === null || hits > 0 || !snapshot) return null;
  const globals = snapshot.state?.globals as
    | { players?: Array<{ playerIndex: number }> }
    | undefined;
  const realPlayers = new Set((globals?.players ?? []).map((p) => p.playerIndex));
  let best: { dist: number; owner: number } | null = null;
  for (const node of snapshot.scene.children ?? []) {
    if (node.type !== 'unit' && node.type !== 'city') continue;
    const c = coord(node);
    if (!c) continue;
    const dist = hexDistance(q, r, c.q, c.r);
    if (dist > radius) continue;
    if (best === null || dist < best.dist) best = { dist, owner: c.owner };
  }
  if (!best || best.owner === localPlayer || !realPlayers.has(best.owner)) return null;
  return 'notAtWar';
}
